import classNames from 'classnames';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import { useSearchContext } from '@/libraries/contexts/search-context';
import styles from './search-bar-brand-selector-button.module.scss';

interface BrandConfig {
  headline: string;
  icon: IconName;
}

// Brands missing from the map (e.g. gruppe) render nothing
const brandMap: Partial<Record<Content.Properties.Brand, BrandConfig>> = {
  default: {
    headline: 'Sol',
    icon: 'Sun',
  },
  golf: {
    headline: 'Bravo Golf',
    icon: 'Golf',
  },
  rejseklubben: {
    headline: 'Rejseklubben',
    icon: 'Luggage',
  },
  safari: {
    headline: 'Bravo Safari',
    icon: 'Safari',
  },
};

export interface SearchBarBrandSelectorButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** Indicates whether the field is active */
  isActive?: boolean;
  /** Indicates whether the field is notified that has changes */
  isNotified?: boolean;
  /** An event that is triggered on click */
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
}

const SearchBarBrandSelectorButton = ({
  className,
  disabled = false,
  isActive = false,
  isNotified = false,
  onClick,
  ...rest
}: SearchBarBrandSelectorButtonProps) => {
  const { brand } = useSearchContext();
  const brandConfig = brandMap[brand];

  if (!brandConfig) return null;

  const { headline, icon } = brandConfig;

  return (
    <button
      {...rest}
      // Stays focusable when disabled, so focus isn't lost when it gets disabled
      // while focused, and the click is blocked in onClick instead
      aria-disabled={disabled || undefined}
      aria-expanded={isActive}
      className={classNames(styles.component, className, {
        [styles.isActive]: isActive,
        [styles.isDisabled]: disabled,
        [styles.isNotified]: isNotified,
      })}
      onClick={event => {
        if (disabled) return;

        onClick?.(event);
      }}
      type="button"
    >
      <div className={styles.brandContent}>
        <div className={styles.brandHeader}>
          <Icon name={icon} size={24} />

          <Typography tagStyle="bodySmallBold">{headline}</Typography>
        </div>

        <Icon
          aria-hidden="true"
          className={styles.brandChevron}
          name="ChevronDown"
          size={14}
        />
      </div>
    </button>
  );
};

SearchBarBrandSelectorButton.displayName = 'SearchBarBrandSelectorButton';

export default SearchBarBrandSelectorButton;
